import { createContext, useContext, ReactNode, useEffect, useState, useCallback } from 'react';
import { useSession, useUser, useDescope, getSessionToken } from '@descope/react-sdk';
import { useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../services/api';

interface User {
  email: string;
  name: string | null;
}

interface AvailableTenant {
  id: string;
  name: string | null;
  is_test: boolean;
  organization_id: string | null;
}

interface Tenant {
  id: string;
  name: string | null;
  is_test: boolean;
  organization_id: string | null;
}

interface AuthContextType {
  user: User | null;
  tenant: Tenant | null;
  availableTenants: AvailableTenant[];
  isAuthenticated: boolean;
  isLoading: boolean;
  logout: () => void;
  getToken: () => string | undefined;
  switchTenant: (tenantId: string) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const { isAuthenticated, isSessionLoading } = useSession();
  const { isUserLoading } = useUser();
  const descopeSdk = useDescope();
  const descopeLogout = descopeSdk.logout;
  const queryClient = useQueryClient();

  const [user, setUser] = useState<User | null>(null);
  const [tenant, setTenant] = useState<Tenant | null>(null);
  const [availableTenants, setAvailableTenants] = useState<AvailableTenant[]>([]);
  const [isLoadingUserInfo, setIsLoadingUserInfo] = useState(false);

  const fetchUserInfo = useCallback(async () => {
    try {
      setIsLoadingUserInfo(true);
      const response = await apiClient.getCurrentUser();
      setUser(response.user);
      setTenant(response.tenant);
      setAvailableTenants(response.available_tenants || []);
    } catch (error) {
      console.error('Failed to fetch user info:', error);
    } finally {
      setIsLoadingUserInfo(false);
    }
  }, []);

  // Provide a token getter that always fetches the freshest Descope session token
  useEffect(() => {
    if (isAuthenticated) {
      apiClient.setTokenGetter(() => getSessionToken());
    } else {
      apiClient.setTokenGetter(null);
    }
  }, [isAuthenticated]);

  // Fetch user info from backend when authenticated
  useEffect(() => {
    if (isAuthenticated && !isSessionLoading && !isUserLoading) {
      fetchUserInfo();
    } else if (!isAuthenticated) {
      setUser(null);
      setTenant(null);
      setAvailableTenants([]);
    }
  }, [isAuthenticated, isSessionLoading, isUserLoading, fetchUserInfo]);

  const logout = () => {
    descopeLogout();
    setUser(null);
    setTenant(null);
    setAvailableTenants([]);
    apiClient.setTokenGetter(null);
  };

  const getToken = () => {
    return getSessionToken();
  };

  const switchTenant = useCallback(async (tenantId: string) => {
    if (!descopeSdk) return;

    try {
      // Use Descope SDK to switch tenant — refreshes the session token
      // with the new dct claim
      await descopeSdk.selectTenant(tenantId);

      // Re-fetch user info (tenant context has changed)
      await fetchUserInfo();

      // Invalidate all queries so pages refetch with new tenant's data
      queryClient.invalidateQueries();
    } catch (error) {
      console.error('Failed to switch tenant:', error);
    }
  }, [descopeSdk, fetchUserInfo, queryClient]);

  const isLoading = isSessionLoading || isUserLoading || isLoadingUserInfo;

  return (
    <AuthContext.Provider
      value={{
        user,
        tenant,
        availableTenants,
        isAuthenticated,
        isLoading,
        logout,
        getToken,
        switchTenant,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
